import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios';
import { hssColors, commonStyles } from '../config/theme';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onFinish = async (values) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post('/login', values);
      const { data } = response;

      if (data.status === 'success' && data.token) {
        const token = data.token.startsWith('Bearer ') ?
          data.token :
          `Bearer ${data.token}`;

        localStorage.setItem('token', token);
        localStorage.setItem('userRol', data.usuario.rol || 'administrativo');
        localStorage.setItem('userName', data.usuario.nombre || '');
        localStorage.setItem('userDni', String(data.usuario.dni || ''));
        axiosInstance.defaults.headers.common['Authorization'] = token;

        message.success(data.message);
        navigate(data.redirect || '/dashboard');
      } else {
        const errorMsg = data.message || 'Error en la autenticación';
        setError(errorMsg);
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('Error de login:', error);
      const errorMsg = error.response?.data?.message || 'Error al iniciar sesión. Verifica tus credenciales.';
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      background: commonStyles.gradientPrimary,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Efectos decorativos de fondo */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-5%',
        width: '600px',
        height: '600px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        filter: 'blur(60px)',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-10%',
        left: '-5%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.08)',
        filter: 'blur(50px)',
      }} />

      {/* Contenedor principal */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        position: 'relative',
        zIndex: 1,
      }}>
        <Card
          style={{
            width: '100%',
            maxWidth: '440px',
            borderRadius: '16px',
            boxShadow: commonStyles.shadowXl,
            border: 'none',
            overflow: 'hidden',
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Header con logo y título */}
          <div style={{
            background: hssColors.backgroundSecondary,
            padding: '48px 40px 32px',
            textAlign: 'center',
            borderBottom: `1px solid ${hssColors.gray100}`,
          }}>
            <img
              src="/logo-hss.png"
              alt="HSS Logo"
              style={{
                maxWidth: '220px',
                height: 'auto',
                marginBottom: '24px',
              }}
            />
            <h2 style={{
              margin: 0,
              fontSize: '28px',
              fontWeight: '700',
              color: hssColors.primary,
              letterSpacing: '-0.5px',
            }}>
              Sistema de Gestión
            </h2>
          </div>

          {/* Formulario */}
          <div style={{ padding: '40px 40px 32px' }}>
            {error && (
              <Alert
                message="Error de Autenticación"
                description={error}
                type="error"
                showIcon
                closable
                onClose={() => setError(null)}
                style={{
                  marginBottom: '24px',
                  borderRadius: '8px',
                }}
              />
            )}

            <Form onFinish={onFinish} size="large">
              <Form.Item
                name="usuario"
                rules={[{ required: true, message: 'Ingrese su usuario' }]}
                style={{ marginBottom: '20px' }}
              >
                <Input
                  prefix={<UserOutlined style={{ color: hssColors.gray400 }} />}
                  placeholder="Usuario"
                  style={{
                    borderRadius: '8px',
                    padding: '12px 16px',
                  }}
                />
              </Form.Item>

              <Form.Item
                name="contrasena"
                rules={[{ required: true, message: 'Ingrese su contraseña' }]}
                style={{ marginBottom: '24px' }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: hssColors.gray400 }} />}
                  placeholder="Contraseña"
                  style={{
                    borderRadius: '8px',
                    padding: '12px 16px',
                  }}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  style={{
                    height: '48px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    boxShadow: commonStyles.shadowMd,
                  }}
                >
                  Ingresar al Sistema
                </Button>
              </Form.Item>
            </Form>
          </div>

        </Card>
      </div>

      {/* Footer con información */}
      <div style={{
        padding: '32px 24px',
        textAlign: 'center',
        color: 'rgba(255, 255, 255, 0.95)',
        fontSize: '13px',
        zIndex: 1,
        marginTop: 'auto',
      }}>
        <p style={{ margin: '0 0 6px 0', fontWeight: '500' }}>
          © 2026 Hunter Security Systems S.A. · Sistema de Gestión
        </p>
        <p style={{ margin: 0, fontSize: '12px', opacity: 0.8 }}>
          Desarrollado por Ing. Rodrigo Melgarejo
        </p>
      </div>
    </div>
  );
};

export default Login;